<template>
  <div
    ref="chart"
    class="echarts-horizontal-bar-chart"
    role="img"
    :aria-label="ariaLabel"
  />
</template>

<script>
import echarts, { chartAnimationDuration } from './echarts-core'

const CURRENCY_FORMATTER = new Intl.NumberFormat('en-CA', {
  style: 'currency',
  currency: 'CAD',
  maximumFractionDigits: 0
})

export default {
  name: 'EChartsHorizontalBarChart',
  props: {
    categories: {
      type: Array,
      required: true
    },
    values: {
      type: Array,
      required: true
    },
    seriesName: {
      type: String,
      default: 'Revenue'
    },
    isDark: {
      type: Boolean,
      default: false
    },
    ariaLabel: {
      type: String,
      default: 'Horizontal bar chart'
    },
    color: {
      type: String,
      default: '#0d2d2c'
    },
    darkColor: {
      type: String,
      default: '#4ADE80'
    },
    valueFormatter: {
      type: Function,
      default: value => CURRENCY_FORMATTER.format(Number(value) || 0)
    },
    compactAxisValueFormatter: {
      type: Function,
      default: null
    }
  },
  data() {
    return {
      chart: null,
      resizeObserver: null
    }
  },
  mounted() {
    this.$nextTick(this.initChart)
  },
  beforeDestroy() {
    this.teardownResize()

    if (this.chart) {
      this.chart.dispose()
      this.chart = null
    }
  },
  watch: {
    categories: {
      handler() {
        this.updateChart()
      },
      deep: true
    },
    values: {
      handler() {
        this.updateChart()
      },
      deep: true
    },
    isDark() {
      this.updateChart()
    }
  },
  methods: {
    initChart() {
      if (!this.$refs.chart || this.chart) return

      this.chart = echarts.init(this.$refs.chart, null, {
        renderer: 'canvas'
      })
      this.bindResize()
      this.updateChart()
    },
    updateChart() {
      if (!this.chart) {
        this.$nextTick(this.initChart)
        return
      }

      this.chart.setOption(this.buildOptions(), true)
      this.refresh()
    },
    refresh() {
      this.$nextTick(() => {
        if (this.chart) {
          this.chart.resize()
        }
      })
    },
    bindResize() {
      window.addEventListener('resize', this.refresh)

      if (typeof ResizeObserver !== 'undefined' && this.$refs.chart) {
        this.resizeObserver = new ResizeObserver(this.refresh)
        this.resizeObserver.observe(this.$refs.chart)
      }
    },
    teardownResize() {
      window.removeEventListener('resize', this.refresh)

      if (this.resizeObserver) {
        this.resizeObserver.disconnect()
        this.resizeObserver = null
      }
    },
    formatValue(value) {
      return this.valueFormatter(value)
    },
    formatCompactAxisValue(value) {
      return this.compactAxisValueFormatter
        ? this.compactAxisValueFormatter(value)
        : this.formatValue(value)
    },
    buildOptions() {
      const textColor = this.isDark ? 'rgba(255,255,255,0.72)' : 'rgba(0,0,0,0.6)'
      const gridColor = this.isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)'
      const axisColor = this.isDark ? 'rgba(255,255,255,0.16)' : 'rgba(0,0,0,0.12)'
      const labelColor = this.isDark ? 'rgba(255,255,255,0.86)' : '#0d2d2c'
      const seriesColor = this.isDark ? this.darkColor : this.color

      return {
        animationDuration: chartAnimationDuration(),
        color: [seriesColor],
        grid: {
          top: 8,
          right: 72,
          bottom: 20,
          left: 8,
          containLabel: true
        },
        tooltip: {
          trigger: 'axis',
          renderMode: 'richText',
          axisPointer: {
            type: 'shadow',
            shadowStyle: {
              color: this.isDark ? 'rgba(255,255,255,0.04)' : 'rgba(13,45,44,0.04)'
            }
          },
          confine: true,
          backgroundColor: this.isDark ? 'rgba(35,35,35,0.96)' : 'rgba(20,20,20,0.88)',
          borderWidth: 0,
          textStyle: {
            color: '#fff',
            fontSize: 12
          },
          formatter: params => {
            const point = Array.isArray(params) ? params[0] : params
            if (!point) return ''
            return [
              point.name,
              `${this.seriesName}: ${this.formatValue(point.value)}`
            ].join('\n')
          }
        },
        xAxis: {
          type: 'value',
          axisLabel: {
            color: textColor,
            formatter: value => this.formatValue(value)
          },
          axisLine: {
            lineStyle: {
              color: axisColor
            }
          },
          axisTick: {
            show: false
          },
          splitLine: {
            lineStyle: {
              color: gridColor
            }
          }
        },
        yAxis: {
          type: 'category',
          inverse: true,
          data: this.categories,
          axisLabel: {
            color: textColor,
            fontWeight: 600,
            interval: 0,
            width: 126,
            overflow: 'truncate'
          },
          axisLine: {
            show: false
          },
          axisTick: {
            show: false
          }
        },
        series: [
          {
            name: this.seriesName,
            type: 'bar',
            data: this.values,
            barWidth: 14,
            itemStyle: {
              borderRadius: [0, 4, 4, 0],
              color: seriesColor
            },
            label: {
              show: true,
              position: 'right',
              color: labelColor,
              fontWeight: 700,
              formatter: params => this.formatValue(params.value)
            },
            emphasis: {
              focus: 'series'
            }
          }
        ],
        media: [
          {
            query: { maxWidth: 700 },
            option: {
              grid: {
                top: 8,
                right: 58,
                bottom: 20,
                left: 0,
                containLabel: true
              },
              xAxis: {
                splitNumber: 3,
                axisLabel: {
                  fontSize: 10,
                  formatter: value => this.formatCompactAxisValue(value)
                }
              },
              yAxis: {
                axisLabel: {
                  fontSize: 10,
                  width: 96
                }
              },
              series: [
                {
                  label: {
                    fontSize: 10
                  }
                }
              ]
            }
          }
        ]
      }
    }
  }
}
</script>

<style scoped lang="scss">
.echarts-horizontal-bar-chart {
  width: 100%;
  height: 100%;
  min-height: 180px;
}
</style>
